import Tippy, { TippyProps } from '@tippyjs/react';
import { CSSProperties, ReactElement } from 'react';
import styled from 'styled-components';
import { AutoFocusContextProvider, useAutoFocus } from '../../../contexts/autofocus-context';
import { BouColor } from '../../../global/bou-colors';
import { updownTippyStyling } from '../tippy-styles/updown';

export type TooltipPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'bottom-start'
  | 'left-start'
  | 'right-start'
  | 'top-end'
  | 'bottom-end'
  | 'left-end'
  | 'right-end';

interface Props {
  tooltipContent: JSX.Element;
  className?: string;
  childClassName?: string;
  style?: CSSProperties;
  children: ReactElement<any>;
  disableHideOnClick?: boolean;
  position?: TooltipPosition;
  open?: boolean;
  onShow?: () => void;
  onShown?: () => void;
  onHide?: () => void;
  onClickOutside?: () => void;
  visible?: boolean;
  trigger?: string; // any combination of "mouseenter", "focus", "click", and "manual", separated by space
  appendTo?: 'parent' | Element | ((ref: Element) => Element);
  maxWidth?: TippyProps['maxWidth'];
}

export default function CustomTippyTooltip(props: Props) {
  return (
    <AutoFocusContextProvider>
      <FocusedTippyTooltip {...props} />
    </AutoFocusContextProvider>
  );
}

function FocusedTippyTooltip(props: Props) {
  const { focus } = useAutoFocus();
  const isControlled = props.visible !== undefined;
  return (
    <StyledTippy
      content={props.tooltipContent}
      trigger={isControlled ? undefined : props.trigger || 'click'}
      visible={props.visible}
      className={props.className}
      interactive
      arrow={true}
      duration={100}
      appendTo={props.appendTo || document.body}
      placement={props.position || 'top'}
      showOnCreate={props.open}
      hideOnClick={isControlled ? undefined : props.disableHideOnClick ? 'toggle' : true}
      maxWidth={props.maxWidth}
      onShow={() => {
        if (props.onShow) {
          props.onShow();
        }
        focus();
        return;
      }}
      onShown={
        props.onShown ||
        (() => {
          return;
        })
      }
      onHide={
        props.onHide ||
        (() => {
          return;
        })
      }
      onClickOutside={
        props.onClickOutside ||
        (() => {
          return;
        })
      }
    >
      <StyledRootElement className={props.childClassName} style={props.style}>
        {props.children}
      </StyledRootElement>
    </StyledTippy>
  );
}

/*
  Need to have the styled element to make children work. See more at
  https://github.com/atomiks/tippy.js-react or read below:
  "If you want to use a component element as a child, ensure you forward the ref to the DOM node...
  ...styled-components v4 does this for you automatically, so it should be seamless when using the styled constructor."
*/

const StyledTippy = styled(Tippy)`
  ${updownTippyStyling}
  max-width: 100vw;
  padding: 1em;
  color: ${BouColor.TOOL_TEXT_BLACK};
  text-align: left;
`;

const StyledRootElement = styled.span`
  :focus {
    outline: none;
  }
`;
